import React from 'react';
import {
  Info,
  Bot,
  BarChart3,
  TrendingUp,
  FileText,
  Shield,
  Lock,
  LayoutGrid,
  ArrowRight,
  HardDrive,
  Sparkles,
  MessageSquare,
  Workflow,
  Database,
  Wrench,
  Activity,
  Mail,
  PlayCircle,
  AlertTriangle,
  Upload
} from 'lucide-react';

const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const DEMO_VIDEO_SOURCES = Array.from(
  new Set([
    apiBaseUrl ? `${apiBaseUrl}/media/demo-video` : '/media/demo-video',
    '/media/demo-video',
    '/Maintenance%20AI%20V2.2.0%20Demo%20.mp4',
    '/data/Maintenance%20AI%20V2.2.0%20Demo%20.mp4'
  ])
);

const quickStartSteps = [
  'Sign in with your account (or continue as guest for temporary access).',
  'Open Home and use Chat for a quick question such as top issues or unit health.',
  'Validate findings in Trends using heatmap and trend charts.',
  'Open Predictions to inspect risk, drivers, and next services for a unit.',
  'Export a report PDF when you need to share findings with planners or supervisors.'
];

const tabGuide = [
  {
    icon: BarChart3,
    title: 'Home',
    description:
      'Fleet overview and Maintenance Intelligence entry point. Use it for quick summaries and to start structured analysis workflows.'
  },
  {
    icon: MessageSquare,
    title: 'Chat (Sidebar)',
    description:
      'Ask plain-English questions, run quick actions, and upload files (Excel/CSV/PDF/Word) for AI-assisted analysis.'
  },
  {
    icon: TrendingUp,
    title: 'Trends',
    description:
      'Explore heatmap, monthly trends, yearly comparisons, and maintenance type distribution to identify recurring patterns.'
  },
  {
    icon: Sparkles,
    title: 'Predictions',
    description:
      'Review unit-level risk assessments, score drivers, trend direction, and next services. Export prediction reports as PDF.'
  },
  {
    icon: HardDrive,
    title: 'Storage',
    description:
      'Manage generated reports and uploaded artifacts. Download or delete files used in analysis workflows.'
  },
  {
    icon: FileText,
    title: 'Contact',
    description:
      'Send issues or improvement requests to administrators with context from your analysis session.'
  }
];

const chatExamples = [
  'Show shift-wise unplanned downtime for last 30 days.',
  'What is the top 10 issued components for the fleet?',
  'Compare TK35 and TK17 for SMU, downtime, and unplanned event count.',
  'Show me the downtime analysis for TK05 in last three months.',
  'How does hot weather affect the truck fleet health?'
];

const maintenanceIntelligenceGuide = [
  {
    title: 'Select a Tool:',
    text: 'Choose a tool card (event count, component analysis, downtime, SLA, precursor patterns) based on your question.'
  },
  {
    title: 'Configure Filters:',
    text: 'Set maintenance type, period, unit, event type, or thresholds. Metadata suggestions load automatically when backend is available.'
  },
  {
    title: 'Run Analysis:',
    text: 'The app sends a structured payload to backend handlers and streams progress status while data is processed.'
  },
  {
    title: 'Review Results:',
    text: 'Use chart panels and markdown summary together. Validate event counts, percentages, and highlighted risk drivers.'
  },
  {
    title: 'Export/Feedback:',
    text: 'Generate PDF reports for meetings and submit thumbs-up/down or comments to improve output quality over time.'
  }
];

const predictionGuide = [
  'Risk level and unit health score are derived from maintenance patterns, trend direction, and comparative fleet baselines.',
  'Risk score drivers identify why risk is high/low (frequency, trend slope, estimated time to likely failure).',
  'Next services are presented from maintenance check logic so planners can prepare upcoming work.',
  'Advisory responses should be read with confidence caveats when data coverage is low.'
];

const securityItems = [
  {
    icon: Lock,
    title: 'Data protection',
    text: 'User data is isolated per account. Chat and analytics artifacts are kept in controlled storage.'
  },
  {
    icon: Shield,
    title: 'Authentication',
    text: 'Access uses secure sign-in and token-based API authorization in production.'
  },
  {
    icon: Database,
    title: 'Transport and storage',
    text: 'HTTPS protects data in transit. Storage and database controls protect persisted records.'
  }
];

const troubleshooting = [
  {
    icon: AlertTriangle,
    title: 'Page not loading or showing errors',
    text: 'Refresh the page first. If it still fails, log out and log in again. If the issue continues, contact administrator.'
  },
  {
    icon: Upload,
    title: 'File upload or analysis failed',
    text: 'Try uploading the file again. If needed, use a smaller file or save it as Excel/CSV/PDF/Word, then retry.'
  },
  {
    icon: Activity,
    title: 'Results look incorrect or unclear',
    text: 'Ask again with clear details like unit name, date range, and maintenance type. Then cross-check in Trends or Predictions.'
  }
];

const sectionCardStyle = {
  background: 'var(--card-bg)',
  borderRadius: 'var(--radius-lg)',
  borderColor: 'var(--card-border)',
  boxShadow: 'var(--card-shadow)'
};

const AboutDashboard = ({ setActiveTab }) => {
  return (
    <div className="space-y-10 animate-fade-in-up">
      <section
        className="rounded-2xl p-6 shadow-sm border"
        style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
      >
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl" style={{ background: 'var(--status-info-bg)' }}>
            <Info className="w-8 h-8" style={{ color: 'var(--status-info)' }} />
          </div>
          <div>
            <h2 className="text-2xl font-bold" style={{ color: 'var(--accent-text)' }}>
              About this platform
            </h2>
            <p className="mt-1 text-sm" style={{ color: 'var(--accent-text-subtle)' }}>
              This smart platform combines Home (with Fleet Overview and Chat), Trends, and Predictions to support daily maintenance decisions.
            </p>
          </div>
        </div>
      </section>

      <section
        className="card p-6 rounded-xl border shadow-sm"
        style={sectionCardStyle}
      >
        <h3 className="text-xl font-bold mb-4 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <PlayCircle className="w-5 h-5 mr-2" style={{ color: 'var(--accent-primary)' }} />
          Demo Video (V2.2.0)
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--accent-text-subtle)' }}>
          Use this walkthrough to see the end-to-end flow: ask in Chat, validate in Trends, and finish in Predictions.
        </p>
        <div className="rounded-xl border p-3" style={{ borderColor: 'var(--card-border)', background: 'var(--bg-tertiary)' }}>
          <video controls preload="metadata" className="w-full rounded-lg" style={{ maxHeight: 460 }}>
            <source src={DEMO_VIDEO_SOURCES[0]} type="video/mp4" />
            <source src={DEMO_VIDEO_SOURCES[1]} type="video/mp4" />
            Your browser cannot play this video.
          </video>
        </div>
        <p className="text-xs mt-3" style={{ color: 'var(--accent-text-subtle)' }}>
          If the player is empty, try with{' '}
          <a
            href="https://adaniaustralia-my.sharepoint.com/:v:/g/personal/zhiwei_chen_bravus_com_au/IQBxL1m8WsstRJsPBIYwIZ_pAfeVi9ltE-L-5MrEy4bIhzk?e=qlA8cs"
            target="_blank"
            rel="noreferrer"
            className="underline"
            style={{ color: 'var(--accent-primary)' }}
          >
            https://adaniaustralia-my.sharepoint.com/:v:/g/personal/zhiwei_chen_bravus_com_au/IQBxL1m8WsstRJsPBIYwIZ_pAfeVi9ltE-L-5MrEy4bIhzk?e=qlA8cs
          </a>
          .
        </p>
      </section>

      <section>
        <h3 className="text-2xl font-bold mb-4 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <Workflow className="w-6 h-6 mr-3" style={{ color: 'var(--accent-primary)' }} />
          Quick Start
        </h3>
        <div className="card p-6 rounded-xl border shadow-sm" style={sectionCardStyle}>
          <ol className="space-y-4">
            {quickStartSteps.map((step, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
                  style={{ background: 'var(--status-info-bg)', color: 'var(--status-info)' }}
                >
                  {idx + 1}
                </span>
                <span className="text-sm leading-relaxed" style={{ color: 'var(--accent-text-subtle)' }}>
                  {step}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold mb-4 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <LayoutGrid className="w-6 h-6 mr-3" style={{ color: 'var(--accent-primary)' }} />
          Tab-by-Tab Guide
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {tabGuide.map((item, idx) => (
            <div
              key={idx}
              className="card p-5 rounded-xl border shadow-sm"
              style={{ ...sectionCardStyle, boxShadow: 'var(--card-shadow)' }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: 'var(--status-neutral-bg)' }}>
                  <item.icon className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                </div>
                <h4 className="font-semibold" style={{ color: 'var(--accent-text)' }}>{item.title}</h4>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--accent-text-subtle)' }}>
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold mb-4 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <Bot className="w-6 h-6 mr-3" style={{ color: 'var(--accent-primary)' }} />
          Chat Usage Examples
        </h3>
        <div className="card p-6 rounded-xl border shadow-sm" style={sectionCardStyle}>
          <p className="text-sm mb-4" style={{ color: 'var(--accent-text-subtle)' }}>
            Use plain-English prompts. Include unit name, period, and maintenance type when possible for higher precision.
          </p>
          <div className="flex flex-wrap gap-2">
            {chatExamples.map((example, idx) => (
              <span
                key={idx}
                className="text-xs px-3 py-1.5 rounded-full border"
                style={{
                  color: 'var(--accent-text)',
                  borderColor: 'var(--card-border)',
                  background: 'var(--bg-tertiary)'
                }}
              >
                {example}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold mb-4 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <Wrench className="w-6 h-6 mr-3" style={{ color: 'var(--accent-primary)' }} />
          Maintenance Intelligence Workflow
        </h3>
        <div className="card p-6 rounded-xl border shadow-sm" style={sectionCardStyle}>
          <ul className="space-y-3">
            {maintenanceIntelligenceGuide.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <ArrowRight className="w-4 h-4 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
                <span className="text-sm" style={{ color: 'var(--accent-text-subtle)' }}>
                  <strong style={{ color: 'var(--accent-text)' }}>{item.title}</strong> {item.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold mb-4 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <Sparkles className="w-6 h-6 mr-3" style={{ color: 'var(--accent-primary)' }} />
          Predictions Guide
        </h3>
        <div className="card p-6 rounded-xl border shadow-sm" style={sectionCardStyle}>
          <ul className="space-y-3">
            {predictionGuide.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <ArrowRight className="w-4 h-4 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
                <span className="text-sm" style={{ color: 'var(--accent-text-subtle)' }}>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold mb-4 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <Shield className="w-6 h-6 mr-3" style={{ color: 'var(--accent-primary)' }} />
          Security and Privacy
        </h3>
        <div className="card p-6 rounded-xl border shadow-sm" style={sectionCardStyle}>
          <div className="space-y-4">
            {securityItems.map((item, idx) => (
              <div key={idx} className="rounded-lg border p-4 flex gap-3" style={{ borderColor: 'var(--card-border)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--status-success-bg)' }}>
                  <item.icon className="w-5 h-5" style={{ color: 'var(--status-success)' }} />
                </div>
                <div>
                  <h4 className="font-semibold" style={{ color: 'var(--accent-text)' }}>{item.title}</h4>
                  <p className="text-sm mt-1" style={{ color: 'var(--accent-text-subtle)' }}>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section>
        <h3 className="text-2xl font-bold mb-4 flex items-center" style={{ color: 'var(--accent-text)' }}>
          <Activity className="w-6 h-6 mr-3" style={{ color: 'var(--accent-primary)' }} />
          Troubleshooting
        </h3>
        <div className="card p-6 rounded-xl border shadow-sm" style={sectionCardStyle}>
          <div className="space-y-4">
            {troubleshooting.map((item, idx) => (
              <div key={idx} className="rounded-lg border p-4 flex gap-3" style={{ borderColor: 'var(--card-border)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--status-warning-bg)' }}>
                  <item.icon className="w-5 h-5" style={{ color: 'var(--status-warning)' }} />
                </div>
                <div>
                  <h4 className="font-semibold" style={{ color: 'var(--accent-text)' }}>{item.title}</h4>
                  <p className="text-sm mt-1" style={{ color: 'var(--accent-text-subtle)' }}>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 p-4 rounded-lg border flex items-start gap-3" style={{ borderColor: 'var(--card-border)', background: 'var(--bg-tertiary)' }}>
            <Mail className="w-4 h-4 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
            <p className="text-xs" style={{ color: 'var(--accent-text-subtle)' }}>
              For complex troubleshoot, please{' '}
              <button
                type="button"
                className="underline font-medium"
                style={{ color: 'var(--accent-primary)' }}
                onClick={() => setActiveTab && setActiveTab('contact')}
              >
                contact administrator
              </button>
              .
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutDashboard;
